import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { EHumanReviewTab } from './human-review-requests.type'
import { isEnumValue } from './human-review-search-params.util'
import { getLastPatientMedEventId } from './last-patient-med-event.util'
import { getLastTab, setLastTab } from './last-tab.util'

// `activeSubject` is null until the URL tells which tab is displayed: a missing or unusable tab is corrected right away.
// Tab priority: the one in the URL, then the last one seen in the project, then the first visible one.
export const useHumanReviewTab = (visibleSubjects: EHumanReviewTab[]) => {
  const { project } = useCurrentProject()
  const [searchParams, setSearchParams] = useSearchParams()

  const rawTab = searchParams.get('tab')
  const tabFromUrl = rawTab !== null && isEnumValue(EHumanReviewTab, rawTab) ? rawTab : null
  const resolvedSubject =
    [tabFromUrl, getLastTab(project.id)].find((tab) => tab && visibleSubjects.includes(tab)) ||
    visibleSubjects[0] ||
    null
  const activeSubject = resolvedSubject === tabFromUrl ? resolvedSubject : null

  useEffect(() => {
    if (!resolvedSubject || resolvedSubject === tabFromUrl) return
    const newParams = new URLSearchParams(searchParams)
    newParams.set('tab', resolvedSubject)
    setSearchParams(newParams, { replace: true })
  }, [resolvedSubject, tabFromUrl, searchParams, setSearchParams])

  useEffect(() => {
    if (activeSubject) setLastTab(project.id, activeSubject)
  }, [project.id, activeSubject])

  const changeTab = (tab: EHumanReviewTab) => {
    const newParams = new URLSearchParams({ tab })
    const lastPatientMedEventId = getLastPatientMedEventId(project.id, tab)
    if (lastPatientMedEventId) newParams.set('patientMedEventId', lastPatientMedEventId.toString())
    setSearchParams(newParams)
  }

  return { activeSubject, changeTab }
}
